import { useState } from "react";
import {
  Bug,
  Maximize2,
  Minimize2,
  NotebookPen,
  Pencil,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { DeviceSettingsDialog } from "./DeviceSettingsDialog";
import { MeetingDebugPanel } from "./MeetingDebugPanel";
import { InviteModal } from "./InviteModal";
import { MeetingControls } from "./MeetingControls";
import { MeetingNotesThread } from "./MeetingNotesThread";
import { VideoTileGrid } from "./VideoTileGrid";
import type { useMediaDevices } from "../hooks/useMediaDevices";
import type { useMeetingRoom } from "../hooks/useMeetingRoom";
import {
  useOccurrence,
  useOccurrenceAction,
  useUpdateMeeting,
} from "../hooks/useMeetings";
import { participantsWord } from "../plural";

type RoomHandle = ReturnType<typeof useMeetingRoom>;
type DevicesHandle = ReturnType<typeof useMediaDevices>;

interface Props {
  occurrenceId: string;
  room: RoomHandle;
  devices: DevicesHandle;
  variant: "full" | "dock";
  /** full → collapse to the side panel. */
  onDock: () => void;
  /** dock → expand to full screen (navigates to the room route). */
  onExpand: () => void;
  /** Leave the meeting for good and clear it from the dock. */
  onLeave: () => void;
}

/**
 * The meeting room UI, driven by an already-connected `room` handle owned by
 * MeetingRoomProvider. Rendered either full-screen (variant="full", on the room
 * route) or compact inside the right-hand dock (variant="dock"). The WebRTC
 * connection is identical in both — only the chrome differs.
 */
export function MeetingRoomView({
  occurrenceId,
  room,
  devices,
  variant,
  onDock,
  onExpand,
  onLeave,
}: Props) {
  const { data: occ } = useOccurrence(occurrenceId);
  const endAction = useOccurrenceAction("end");
  const updateMeeting = useUpdateMeeting(occ?.meeting_id ?? "");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [debugOpen, setDebugOpen] = useState(false);
  const isDeveloper = useAuthStore((s) => s.user?.is_developer === true);
  const isDock = variant === "dock";
  const canEditTitle = !isDock && !!occ?.is_organizer;

  const startEditTitle = () => {
    if (!canEditTitle || !occ) return;
    setTitleDraft(occ.title);
    setEditingTitle(true);
  };

  const saveTitle = async () => {
    const next = titleDraft.trim();
    setEditingTitle(false);
    if (!occ || !next || next === occ.title) return;
    await updateMeeting.mutateAsync({ title: next });
  };

  if (room.status === "ended") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-4">
        <p className="text-sm text-[var(--color-text-secondary)]">
          Spotkanie zakończone.
        </p>
        <button
          type="button"
          onClick={onLeave}
          className="rounded-md bg-[var(--color-primary)] px-4 py-1.5 text-sm text-white"
        >
          Zamknij
        </button>
      </div>
    );
  }

  if (room.status === "error") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-4">
        <p className="text-center text-sm text-red-500">{room.error}</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void room.join()}
            className="rounded-md bg-[var(--color-primary)] px-4 py-1.5 text-sm text-white"
          >
            Spróbuj ponownie
          </button>
          <button
            type="button"
            onClick={onLeave}
            className="rounded-md border border-[var(--color-border)] px-4 py-1.5 text-sm text-[var(--color-text-secondary)]"
          >
            Zamknij
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--color-bg-body)]">
      <div className="flex items-center justify-between gap-2 border-b border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2">
        <div className="min-w-0">
          {editingTitle ? (
            <input
              type="text"
              autoFocus
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={() => void saveTitle()}
              onKeyDown={(e) => {
                if (e.key === "Enter") void saveTitle();
                if (e.key === "Escape") setEditingTitle(false);
              }}
              className="w-full rounded border border-[var(--color-primary)] bg-[var(--color-bg-surface)] px-1.5 py-0.5 text-sm font-semibold text-[var(--color-text-primary)] focus:outline-none"
            />
          ) : (
            <div className="flex items-center gap-1.5">
              <h1
                className={`truncate text-sm font-semibold text-[var(--color-text-primary)] ${
                  canEditTitle ? "cursor-text" : ""
                }`}
                title={
                  canEditTitle ? "Kliknij dwukrotnie, aby zmienić nazwę" : undefined
                }
                onDoubleClick={startEditTitle}
              >
                {occ?.title ?? "Spotkanie"}
              </h1>
              {canEditTitle && (
                <button
                  type="button"
                  title="Zmień nazwę"
                  onClick={startEditTitle}
                  className="shrink-0 rounded p-0.5 text-[var(--color-text-muted)] hover:bg-black/5 hover:text-[var(--color-text-primary)] dark:hover:bg-white/10"
                >
                  <Pencil size={12} />
                </button>
              )}
            </div>
          )}
          <p className="text-xs text-[var(--color-text-muted)]">
            <Users size={11} className="mr-1 inline" />
            {room.peers.length + 1} {participantsWord(room.peers.length + 1)}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {!isDock && occ?.is_organizer && (
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              className="flex items-center gap-1.5 rounded-md border border-[var(--color-border)] px-2.5 py-1.5 text-xs text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
            >
              <UserPlus size={13} /> Zaproś
            </button>
          )}
          {!isDock && isDeveloper && (
            <button
              type="button"
              title="Logi RTC na żywo (tylko dev)"
              onClick={() => setDebugOpen((o) => !o)}
              className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs ${
                debugOpen
                  ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                  : "border-[var(--color-border)] text-[var(--color-text-secondary)]"
              }`}
            >
              <Bug size={13} /> Logi
            </button>
          )}
          {!isDock && (
            <button
              type="button"
              onClick={() => setNotesOpen((o) => !o)}
              className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs ${
                notesOpen
                  ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                  : "border-[var(--color-border)] text-[var(--color-text-secondary)]"
              }`}
            >
              <NotebookPen size={13} /> Notatki
            </button>
          )}
          {isDock ? (
            <button
              type="button"
              title="Rozwiń na cały ekran"
              onClick={onExpand}
              className="rounded-md border border-[var(--color-border)] p-1.5 text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
            >
              <Maximize2 size={14} />
            </button>
          ) : (
            <button
              type="button"
              title="Zbij do panelu"
              onClick={onDock}
              className="flex items-center gap-1.5 rounded-md border border-[var(--color-border)] px-2.5 py-1.5 text-xs text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
            >
              <Minimize2 size={13} /> Zbij do panelu
            </button>
          )}
        </div>
      </div>

      {room.cameraAutoDisabled && room.status === "active" && !isDock && (
        <div className="border-b border-amber-300 bg-amber-50 px-4 py-1.5 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
          Duże spotkanie — kamera wyłączona przy wejściu, aby oszczędzić łącze.
          Możesz ją włączyć przyciskiem poniżej.
        </div>
      )}

      {room.error && room.status === "active" && (
        <div className="flex items-center justify-between gap-3 border-b border-red-300 bg-red-50 px-4 py-1.5 text-xs text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300">
          <span className="truncate">{room.error}</span>
          <button
            type="button"
            onClick={room.dismissError}
            className="shrink-0 font-medium hover:underline"
          >
            OK
          </button>
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 bg-[var(--color-bg-body)]">
          {room.status === "joining" || room.status === "idle" ? (
            <div className="flex h-full items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <VideoTileGrid
              me={room.me}
              localCameraStream={room.localCameraStream}
              localScreenStream={room.localScreenStream}
              localMediaState={room.mediaState}
              peers={room.peers}
              speakerId={devices.prefs.speakerId}
              onDiagnostic={room.logEvent}
            />
          )}
        </div>

        {!isDock && notesOpen && occ && (
          <aside className="w-96 shrink-0 overflow-y-auto border-l border-[var(--color-border)] bg-[var(--color-bg-surface)] p-3">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
                Notatki
              </h2>
              <button type="button" onClick={() => setNotesOpen(false)}>
                <X size={14} className="text-[var(--color-text-muted)]" />
              </button>
            </div>
            <MeetingNotesThread
              occurrenceId={occ.id}
              occurrenceStatus="live"
              isOrganizer={occ.is_organizer}
              compact
              meetingId={occ.meeting_id}
              guestNotesEnabled={occ.guests_can_see_notes}
            />
          </aside>
        )}
      </div>

      {!isDock && isDeveloper && debugOpen && (
        <MeetingDebugPanel onClose={() => setDebugOpen(false)} />
      )}

      <MeetingControls
        mediaState={room.mediaState}
        onToggleMic={room.toggleMic}
        onToggleCamera={() => void room.toggleCamera()}
        onToggleScreen={() =>
          room.mediaState.screen
            ? room.stopScreenShare()
            : void room.startScreenShare()
        }
        onOpenSettings={() => setSettingsOpen(true)}
        onLeave={onLeave}
        onEndForAll={
          occ?.is_organizer
            ? () => {
                void endAction.mutateAsync(occ.id).then(() => onLeave());
              }
            : null
        }
      />

      <DeviceSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        devices={devices}
        onSwitchMic={(id) => void room.switchMic(id)}
        onSwitchCamera={(id) => void room.switchCamera(id)}
        onRequestAccess={async () => {
          const ok = await room.requestDevices();
          await devices.refresh();
          return ok;
        }}
        hasMedia={!!room.localCameraStream}
      />

      {!isDock && occ?.is_organizer && (
        <InviteModal
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          meetingId={occ.meeting_id}
          occurrenceId={occ.id}
        />
      )}
    </div>
  );
}
